import Countdown from 'react-countdown';
import { useForm } from 'react-hook-form';
import Turnstile, { TurnstileCallbacks } from 'react-turnstile';
import * as Yup from 'yup';

import config from '@config';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Text, Group, Button, TextInput, Anchor } from '@ui';

interface PolicyDetailsForm {
  policyNumber: string;
  turnstileToken: string;
}

interface PolicyVerificationPolicyStepProps {
  onBack: () => void;
  onSendOTP: (policyNumber: string, turnstileToken: string) => void;
  onNext?: () => void;
  loading?: boolean;
  cooldownUntil?: number;
  oldPolicyNumber?: string;
}

// Validation schema for policy number and turnstile token
const validationSchema = Yup.object().shape({
  policyNumber: Yup.string()
    .required('Policy number is required')
    .min(1, 'Policy number cannot be empty'),
  turnstileToken: Yup.string().required('Security verification required')
});

const TurnstileWidget = (props: TurnstileCallbacks) => {
  return <Turnstile sitekey={config.TURN_STILE_TOKEN} onVerify={props.onVerify} />;
};

const CoolDown = ({
  minutes,
  seconds,
  completed
}: {
  minutes: number;
  seconds: number;
  completed: boolean;
}) => {
  if (completed) {
    return null;
  }
  return (
    <Text fz={14} c="red" ta="center" mt={8}>
      Please wait {minutes}:{seconds.toString().padStart(2, '0')} before trying again
    </Text>
  );
};

export const PolicyVerificationPolicyStep: React.FC<
  PolicyVerificationPolicyStepProps
> = ({
  onBack,
  onSendOTP,
  onNext,
  loading = false,
  cooldownUntil,
  oldPolicyNumber
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue
  } = useForm<PolicyDetailsForm>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      policyNumber: oldPolicyNumber || '',
      turnstileToken: ''
    }
  });

  const isCooldownActive = !!(cooldownUntil && Date.now() < cooldownUntil);

  const onSubmit = async (data: PolicyDetailsForm) => {
    onSendOTP(data.policyNumber, data.turnstileToken);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={24}>
        <Text fz={14} lh={1.5}>
          Please enter your issued policy number. We&apos;ll send an OTP to your
          registered mobile number for verification.
        </Text>

        <Stack gap="md">
          <TextInput
            {...register('policyNumber')}
            label="Policy Number"
            placeholder="Enter your policy number"
            size="xl"
            withAsterisk
            error={errors.policyNumber?.message}
            disabled={isCooldownActive}
          />
          <input type="hidden" {...register('turnstileToken')} />
        </Stack>

        {isCooldownActive && cooldownUntil && (
          <Countdown
            key={`cooldown-${cooldownUntil}`}
            date={cooldownUntil}
            renderer={CoolDown}
          />
        )}

        <Text fz={12} c="dimmed">
          Can&apos;t find your policy number? You can find it from your account,{' '}
          <Anchor href="/login" target="_blank" rel="noopener noreferrer" fz={12}>
            click here
          </Anchor>{' '}
          to login.
        </Text>

        <Group justify="center">
          <TurnstileWidget
            onVerify={(token: string) => {
              setValue('turnstileToken', token, { shouldValidate: true });
            }}
          />
        </Group>

        <Group gap="xs" wrap="nowrap">
          <Button onClick={onBack} variant="outline" fullWidth size="xl">
            Back
          </Button>
          {isCooldownActive && onNext ? (
            <Button onClick={onNext} fullWidth size="xl">
              Enter OTP
            </Button>
          ) : (
            <Button
              type="submit"
              fullWidth
              size="xl"
              loading={loading}
              disabled={loading || !isValid || isCooldownActive}
            >
              Send OTP
            </Button>
          )}
        </Group>
      </Stack>
    </form>
  );
};
